import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiUrl, parseApiResponse } from '../utils/api';
import { formatDateTime } from '../utils/helpers';

export default function ColisDetailsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = (params.get('code') || '').trim();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [colis, setColis] = useState(null);

  const photoList = useMemo(() => {
    const pathname = window.location.pathname || '/';
    const firstSeg = pathname.split('/').filter(Boolean)[0] || '';
    const basePath = firstSeg === 'express' ? '/express' : '';
    const raw = [];
    if (colis?.photo) raw.push(colis.photo);
    if (Array.isArray(colis?.meta?.photos)) {
      for (const p of colis.meta.photos) {
        if (p) raw.push(p);
      }
    }

    const resolved = raw
      .map((p) => {
        const s = String(p || '').trim();
        if (!s) return '';
        if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
        if (s.startsWith('//')) return `${window.location.protocol}${s}`;
        if (s.startsWith('uploads/')) {
          return basePath ? `${basePath}/${s}` : `/${s}`;
        }
        if (s.startsWith('/')) {
          if (basePath && s.startsWith('/uploads/')) return `${basePath}${s}`;
          return s;
        }
        return s;
      })
      .filter(Boolean);

    return Array.from(new Set(resolved));
  }, [colis]);

  const apiEndpoint = useMemo(() => {
    const base = apiUrl('public/colis');
    return `${base}&code=${encodeURIComponent(code)}`;
  }, [code]);

  useEffect(() => {
    if (!code) {
      setErr('Code requis');
      setColis(null);
      return;
    }

    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await fetch(apiEndpoint, { method: 'GET' });
        const out = await parseApiResponse(res);
        if (!out.ok) {
          const msg = out?.json?.message || out?.text || 'Erreur serveur';
          throw new Error(String(msg));
        }
        const c = out?.json?.colis || null;
        if (alive) setColis(c);
      } catch (e) {
        if (alive) {
          setErr(e?.message || 'Erreur');
          setColis(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [apiEndpoint, code]);

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '22px 14px' }}>
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#4F46E5', fontWeight: 900, fontSize: 14,
          fontFamily: 'inherit', padding: 0,
        }}>
          ← Retour
        </button>
      </div>

      <div style={{ background: 'white', border: '1px solid #eef2f7', borderRadius: 16, padding: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 18, fontWeight: 1000, color: '#0f172a', marginBottom: 6 }}>Détails du colis</div>
        <div style={{ fontFamily: 'monospace', fontWeight: 900, color: '#4F46E5', marginBottom: 14 }}>{code || '—'}</div>

        {loading && <div style={{ color: '#64748b', fontWeight: 700 }}>Chargement...</div>}
        {!loading && err && <div style={{ color: '#b91c1c', fontWeight: 800 }}>{err}</div>}

        {!loading && !err && colis && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            <Row label="Nom">{colis?.nom || '—'}</Row>
            <Row label="Expéditeur">{colis?.expediteur || '—'}</Row>
            <Row label="Destinataire">{colis?.destinataire || '—'}</Row>
            <Row label="Téléphone">{colis?.tel || '—'}</Row>
            <Row label="Adresse">{colis?.adresse || '—'}</Row>
            <Row label="Zone">{colis?.zone || '—'}</Row>
            <Row label="Statut">{colis?.statut || '—'}</Row>
            <Row label="Livreur">{colis?.livreur || '—'}</Row>
            <Row label="Date">{colis?.date ? formatDateTime(colis.date) : '—'}</Row>

            {photoList.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: '#334155', marginBottom: 8 }}>Photos</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {photoList.map((p) => (
                    <a key={p} href={p} target="_blank" rel="noreferrer" style={{ display: 'inline-block' }}>
                      <img
                        src={p}
                        alt="Photo colis"
                        width={110}
                        height={110}
                        style={{ borderRadius: 14, objectFit: 'cover', border: '1px solid #e2e8f0', background: 'white' }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, alignItems: 'baseline' }}>
      <div style={{ fontSize: 12, fontWeight: 900, color: '#334155' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}
